import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BrandMark from "../components/BrandMark";
import { useAuth } from "../contexts/AuthContext";
import {
  benefits,
  coach,
  ctaLabel,
  ctaLabelShort,
  openProgramLabel,
  sprintCtaLabel,
  faqItems,
  finalCta,
  handNotes,
  hero,
  inSeasonNote,
  meta,
  offer,
  phases,
  price,
  programFacts,
  proofStats,
  sampleWeek,
  sampleWeekNote,
  sports,
  steps,
  testimonials,
  tickerItems,
  whyFirst20,
} from "../content/landingCopy";

const navLinks = [
  { href: "#inside", label: "What's inside" },
  { href: "#program", label: "The program" },
  { href: "#coach", label: "Tori" },
  { href: "#offer", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const NUMBER_PATTERN =
  /(\$?\d[\d.,]*(?:\s?(?:–|-|→|×)\s?\$?\d[\d.,]*)?\+?)/g;

/**
 * Renders copy. Any [bracketed placeholder] gets a dashed pink highlight so
 * unfinished content is obvious. With `numbers`, digits render in Geist Mono.
 */
function Copy({ text, numbers = false }: { text: string; numbers?: boolean }) {
  const parts = text.split(/(\[[^\]]+\])/g);

  const renderPlain = (part: string, key: number) => {
    if (!numbers) return <React.Fragment key={key}>{part}</React.Fragment>;
    return (
      <React.Fragment key={key}>
        {part.split(NUMBER_PATTERN).map((chunk, j) =>
          j % 2 === 1 ? (
            <span key={j} className="font-mono tabular-nums">
              {chunk}
            </span>
          ) : (
            chunk
          ),
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      {parts.map((part, i) =>
        /^\[.*\]$/.test(part) ? (
          <mark
            key={i}
            className="bg-pink-100 text-pink-800 rounded px-1 outline outline-1 outline-dashed outline-pink-400"
            title="Placeholder: replace before launch"
          >
            {part}
          </mark>
        ) : (
          renderPlain(part, i)
        ),
      )}
    </>
  );
}

/** Fades and lifts children in when they scroll into view. */
function Reveal({
  children,
  className = "",
  delay = 0,
  as = "div",
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  as?: "div" | "li";
}) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(
    () => prefersReducedMotion() || typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    if (visible || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);

  const Tag = as as any;
  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

/** Handwritten note in Caveat. */
function HandNote({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`font-hand text-2xl sm:text-3xl leading-none select-none ${className}`}
    >
      {children}
    </span>
  );
}

function HandArrow({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 80 60"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M6 10 C 28 2, 58 12, 66 46" />
      <path d="M54 38 L66 47 L72 33" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M3 10a.75.75 0 0 1 .75-.75h10.64l-3.72-3.72a.75.75 0 1 1 1.06-1.06l5 5a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 1 1-1.06-1.06l3.72-3.72H3.75A.75.75 0 0 1 3 10Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-6 h-6"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
      />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`w-5 h-5 shrink-0 text-base-content/40 transition-transform duration-200 ${
        open ? "rotate-180" : ""
      }`}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CheckIcon({ className = "text-primary" }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`w-5 h-5 shrink-0 mt-0.5 ${className}`}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.58l7.3-7.3a1 1 0 0 1 1.4 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const laneLinesDark = {
  backgroundImage:
    "repeating-linear-gradient(0deg, transparent 0 63px, rgba(255,255,255,0.07) 63px 64px)",
};

/**
 * Animated 20 m race: a stopwatch in Geist Mono and three lanes. The volt
 * runner (you) accelerates past two ghost runners. Purely decorative.
 */
function SprintTrack() {
  const RUN = 2.9;
  const HOLD = 1.5;
  const [elapsed, setElapsed] = useState(() =>
    prefersReducedMotion() ? RUN : 0,
  );

  useEffect(() => {
    if (prefersReducedMotion()) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const cycle = ((now - start) / 1000) % (RUN + HOLD);
      setElapsed(Math.min(cycle, RUN));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const progress = Math.pow(elapsed / RUN, 1.6);
  const finished = elapsed >= RUN;
  const lanes = [
    { factor: 0.84, you: false },
    { factor: 1, you: true },
    { factor: 0.9, you: false },
  ];

  return (
    <div
      className="relative rounded-md bg-blue-900 text-white p-6 sm:p-8 shadow-2xl overflow-hidden"
      style={laneLinesDark}
      aria-hidden="true"
    >
      <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest text-white/60">
        <span>20 m sprint</span>
        <span
          className={`rounded-md px-2.5 py-1 transition-colors ${
            finished ? "bg-volt text-blue-900" : "bg-white/10"
          }`}
        >
          {finished ? "Finish" : "Running"}
        </span>
      </div>

      <div className="mt-8 font-mono tabular-nums font-semibold leading-none">
        <span className="text-7xl sm:text-8xl">{elapsed.toFixed(2)}</span>
        <span className="text-2xl sm:text-3xl text-white/60 ml-2">s</span>
      </div>

      <div className="mt-10 relative">
        <div className="absolute inset-y-0 left-[6%] w-0.5 bg-white/70" />
        <div className="absolute inset-y-0 left-[50%] w-px bg-white/20" />
        <div className="absolute inset-y-0 left-[94%] w-0.5 bg-volt" />
        {lanes.map((lane, i) => {
          const p = Math.min(progress * lane.factor, 1);
          const left = 6 + p * 88;
          return (
            <div
              key={i}
              className="relative h-12 border-b border-white/15 first:border-t"
            >
              {lane.you && (
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-gradient-to-r from-volt/0 to-volt/70"
                  style={{ left: "6%", width: `${left - 6}%` }}
                />
              )}
              <div
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full ${
                  lane.you
                    ? "w-5 h-5 bg-volt shadow-[0_0_20px_rgba(214,255,58,0.8)]"
                    : "w-3.5 h-3.5 bg-white/35"
                }`}
                style={{ left: `${left}%` }}
              />
              {lane.you && (
                <span
                  className="absolute -top-1 font-mono text-[10px] uppercase tracking-widest text-volt -translate-x-1/2"
                  style={{ left: `${left}%` }}
                >
                  You
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="relative mt-3 h-4 font-mono text-xs text-white/60 whitespace-nowrap">
        <span className="absolute left-[6%] -translate-x-1/2">0 m</span>
        <span className="absolute left-[50%] -translate-x-1/2">10 m</span>
        <span className="absolute left-[94%] -translate-x-1/2 text-volt">
          20 m
        </span>
      </div>
    </div>
  );
}

function HeroVisual() {
  const { media, mediaCallout } = hero;

  return (
    <div className="relative max-w-md mx-auto md:ml-auto md:mr-0 w-full">
      <div className="absolute -top-14 left-4 flex items-end gap-1 text-primary">
        <HandNote className="-rotate-6 whitespace-nowrap">
          {handNotes.hero}
        </HandNote>
        <HandArrow className="w-10 h-9 rotate-[20deg] translate-y-3" />
      </div>
      {media.src ? (
        <div className="relative aspect-[4/5] rounded-md overflow-hidden bg-blue-900 shadow-2xl">
          {media.type === "video" ? (
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src={media.src}
              poster={media.poster || undefined}
              autoPlay
              muted
              loop
              playsInline
              aria-label={media.alt}
            />
          ) : (
            <img
              className="absolute inset-0 w-full h-full object-cover"
              src={media.src}
              alt={media.alt}
            />
          )}
        </div>
      ) : (
        <SprintTrack />
      )}

      <div className="absolute -bottom-10 left-3 sm:-left-10 -rotate-2 max-w-xs rounded-md bg-base-100 border border-base-300 shadow-xl px-5 py-4">
        {mediaCallout.label ? (
          <p className="text-xs font-grotesk text-base-content/60">
            {mediaCallout.label}
          </p>
        ) : null}
        <p className="text-base font-grotesk text-base-content leading-snug">
          <Copy text={mediaCallout.value} />
        </p>
      </div>
    </div>
  );
}

function Ticker() {
  const items = [...tickerItems, ...tickerItems];
  return (
    <div className="relative -rotate-1 bg-volt text-blue-900 py-3 overflow-hidden border-y-2 border-blue-900">
      <div className="flex w-max animate-marquee motion-reduce:animate-none">
        {items.map((item, i) => (
          <span
            key={i}
            className="flex items-center font-mono uppercase tracking-wider text-sm sm:text-base font-semibold"
            aria-hidden={i >= tickerItems.length}
          >
            <span className="px-6">{item}</span>
            <span>✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function FAQItem({
  question,
  answer,
  index,
}: {
  question: string;
  answer: string;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const id = question.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <div className="border-b border-base-300">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group w-full flex items-center gap-4 py-5 text-left font-grotesk font-medium text-base-content focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        aria-expanded={isOpen}
        aria-controls={`faq-${id}`}
      >
        <span className="font-mono text-sm text-primary tabular-nums">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 group-hover:text-primary transition-colors">
          {question}
        </span>
        <ChevronIcon open={isOpen} />
      </button>
      <div
        id={`faq-${id}`}
        className={`grid transition-all duration-300 ${
          isOpen ? "grid-rows-[1fr] pb-5" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="pl-10 text-base-content/70 font-grotesk leading-relaxed">
            <Copy text={answer} numbers />
          </p>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  intro,
  center = false,
  dark = false,
  note,
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
  center?: boolean;
  dark?: boolean;
  note?: string;
}) {
  return (
    <Reveal className={`relative mb-12 max-w-2xl ${center ? "text-center mx-auto" : ""}`}>
      {eyebrow && (
        <p
          className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest mb-4 ${
            dark ? "text-volt" : "text-primary"
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full ${dark ? "bg-volt" : "bg-primary"}`}
          />
          {eyebrow}
        </p>
      )}
      <h2 className="text-4xl md:text-6xl font-extrabold font-heading leading-[1.05] tracking-tight">
        {title}
      </h2>
      {intro && (
        <p
          className={`text-lg font-grotesk mt-5 leading-relaxed ${
            dark ? "text-white/70" : "text-base-content/70"
          }`}
        >
          <Copy text={intro} numbers />
        </p>
      )}
      {note && (
        <HandNote className="hidden md:block absolute -right-44 top-6 rotate-6 text-primary">
          {note}
        </HandNote>
      )}
    </Reveal>
  );
}

const phaseColors = [
  { bar: "bg-secondary", text: "text-blue-900", border: "border-t-secondary" },
  { bar: "bg-primary", text: "text-white", border: "border-t-primary" },
  { bar: "bg-volt", text: "text-blue-900", border: "border-t-volt" },
];

const sportStyles = [
  "bg-primary text-white",
  "bg-blue-900 text-white",
  "bg-volt text-blue-900",
];

const noteTilts = ["-rotate-2", "rotate-1", "-rotate-1"];

function MonotoneSwitch({
  on,
  onToggle,
}: {
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      className="inline-flex items-center gap-2 font-mono text-sm text-base-content"
    >
      <span>Monotone</span>
      <span
        className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-md border border-base-300 ${
          on ? "bg-base-content" : "bg-base-200"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-sm bg-base-100 shadow-sm transition-transform ${
            on ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [monotone, setMonotone] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("monotone", monotone);
    return () => document.documentElement.classList.remove("monotone");
  }, [monotone]);
  const ctaTo = user ? "/home" : "/signup";
  const ctaText = user ? openProgramLabel : ctaLabel;
  const ctaTextShort = user ? openProgramLabel : ctaLabelShort;
  const [priceAmount, priceCurrency] = price.split(" ");

  useEffect(() => {
    document.title = meta.title;
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 0);
      setShowStickyCta(window.scrollY > 640);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const primaryButtonClass =
    "group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold bg-primary text-primary-content rounded-md font-grotesk shadow-[0_6px_0_#005a7a] hover:-translate-y-0.5 hover:shadow-[0_8px_0_#005a7a] active:translate-y-1 active:shadow-[0_2px_0_#005a7a] transition-all focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary w-full sm:w-auto text-center";

  const sprintButtonClass =
    "inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold bg-volt text-blue-900 rounded-md font-grotesk shadow-[0_6px_0_#8fae1a] hover:-translate-y-0.5 hover:shadow-[0_8px_0_#8fae1a] active:translate-y-1 active:shadow-[0_2px_0_#8fae1a] transition-all focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-volt w-full sm:w-auto text-center";

  const headerButtonClass =
    "inline-flex items-center justify-center px-4 py-2 sm:px-5 sm:py-2.5 text-sm font-semibold bg-primary text-primary-content rounded-md font-grotesk hover:opacity-85 transition-opacity focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary whitespace-nowrap";

  return (
    <div className="bg-base-100 text-base-content overflow-x-hidden">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          isScrolled || menuOpen
            ? "bg-base-100 shadow-sm"
            : "bg-base-100/80 backdrop-blur"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <a href="#top" className="shrink-0 hover:opacity-80 transition-opacity">
            <BrandMark />
          </a>

          <nav className="hidden lg:flex items-center gap-6 font-mono text-sm">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {!user && (
              <Link
                to="/login"
                className="hidden sm:inline text-sm font-mono text-base-content/80 hover:text-base-content"
              >
                Log in
              </Link>
            )}
            <Link to={ctaTo} className={headerButtonClass}>
              <span className="sm:hidden">{ctaTextShort}</span>
              <span className="hidden sm:inline">{ctaText}</span>
            </Link>
            <button
              type="button"
              className="lg:hidden p-2 rounded-md border border-base-300"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <MenuIcon />
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="lg:hidden border-t border-base-200 bg-base-100 px-4 py-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={closeMenu}
                className="block py-3 font-mono text-base-content/80 hover:text-base-content"
              >
                {link.label}
              </a>
            ))}
            {!user && (
              <Link
                to="/login"
                onClick={closeMenu}
                className="block py-3 font-mono text-base-content/80 hover:text-base-content"
              >
                Log in
              </Link>
            )}
          </nav>
        )}
      </header>

      <main id="top">
        {/* Hero */}
        <section className="relative pt-28 pb-28 md:pt-36 md:pb-36 px-4 sm:px-6">
          <div
            className="absolute inset-0 -z-0 pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent)]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent 0 71px, rgba(0,150,199,0.08) 71px 72px)",
            }}
          />
          <div className="relative max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-20 md:gap-16">
            <div className="md:w-[55%] flex flex-col gap-6">
              <p className="inline-flex self-start items-center gap-2 rounded-md border border-base-300 bg-base-100 px-3 py-1.5 font-mono text-[11px] sm:text-xs uppercase tracking-widest text-base-content/70">
                <span className="w-2 h-2 rounded-full bg-volt ring-2 ring-blue-900" />
                {hero.eyebrow}
              </p>
              <h1 className="relative isolate text-5xl sm:text-7xl lg:text-8xl font-extrabold font-heading leading-[0.95] tracking-tight text-pretty">
                {hero.title}
              </h1>
              <p className="text-lg sm:text-xl text-base-content/70 font-grotesk leading-relaxed max-w-xl">
                {hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full pt-2">
                <Link to={ctaTo} className={primaryButtonClass}>
                  <span>{ctaText}</span>
                  <ArrowIcon />
                </Link>
                <Link to="/20m" className={sprintButtonClass}>
                  {sprintCtaLabel}
                </Link>
              </div>
              <a
                href="#coach"
                className="group inline-flex self-start items-center gap-3 pt-2"
              >
                <img
                  src={coach.photo}
                  alt=""
                  className="w-11 h-11 rounded-full object-cover object-[center_18%] ring-4 ring-volt/40"
                />
                <span className="font-grotesk text-sm text-base-content/70 group-hover:text-base-content">
                  <Copy text={hero.trustLine} />
                  <span className="block font-hand text-xl text-primary leading-none mt-0.5">
                    meet your coach ↓
                  </span>
                </span>
              </a>
            </div>

            <div className="md:w-[45%]">
              <HeroVisual />
            </div>
          </div>
        </section>

        <Ticker />

        {proofStats.length > 0 && (
          <section className="px-4 sm:px-6 py-14">
            <dl className="max-w-5xl mx-auto grid grid-cols-3 gap-3 sm:gap-6 text-center">
              {proofStats.map((stat, i) => (
                <Reveal key={stat.label} delay={i * 100}>
                  <dt className="sr-only">{stat.label}</dt>
                  <dd className="text-2xl sm:text-4xl md:text-5xl font-semibold font-mono tabular-nums tracking-tight">
                    <Copy text={stat.value} />
                  </dd>
                  <dd className="text-xs sm:text-sm text-base-content/60 font-grotesk mt-2">
                    {stat.label}
                  </dd>
                </Reveal>
              ))}
            </dl>
          </section>
        )}

        {/* Why + benefits */}
        <section id="benefits" className="scroll-mt-24 py-16 md:py-24 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              eyebrow="What you improve"
              title={whyFirst20.title}
              intro={whyFirst20.intro}
              note={handNotes.benefits}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {benefits.map((benefit, index) => (
                <Reveal key={benefit.title} delay={index * 80}>
                  <div className="h-full flex flex-col rounded-md bg-base-200 p-7 transition-transform duration-300 hover:-translate-y-1 hover:-rotate-[0.6deg]">
                    <span className="font-mono text-4xl font-semibold text-primary/30">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-xl font-bold font-grotesk mt-3 mb-2">
                      {benefit.title}
                    </h3>
                    {benefit.body ? (
                      <p className="text-base-content/70 font-grotesk leading-relaxed">
                        {benefit.body}
                      </p>
                    ) : null}
                    <p className="mt-auto pt-6 font-mono text-xs uppercase tracking-widest text-primary">
                      Measured with
                    </p>
                    <p className="mt-1 font-grotesk font-semibold">
                      <Copy text={benefit.test} numbers />
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section
          id="inside"
          className="scroll-mt-24 bg-base-200 py-16 md:py-24 px-4 sm:px-6"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              eyebrow="What's inside"
              title="Everything you need to know up front"
            />
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {programFacts.map((fact, i) => (
                <Reveal key={fact.label} delay={i * 60}>
                  <div className="group relative h-full overflow-hidden rounded-md bg-base-100 p-5 sm:p-6">
                    <span
                      aria-hidden="true"
                      className="absolute inset-x-0 bottom-0 h-1 bg-primary/20 transition-colors group-hover:bg-primary"
                    />
                    <dt className="font-mono text-[11px] uppercase tracking-widest text-base-content/50">
                      {fact.label}
                    </dt>
                    <dd className="text-lg sm:text-xl font-bold font-grotesk mt-2">
                      <Copy text={fact.value} numbers />
                    </dd>
                  </div>
                </Reveal>
              ))}
            </dl>
          </div>
        </section>

        {/* Program structure + sample week */}
        <section id="program" className="scroll-mt-24 py-16 md:py-24 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              eyebrow="The program"
              title="Three blocks, one goal"
              intro="Each block builds on the last. You test in weeks 1, 4 and 11: a 20 m sprint, a 30 m fly, and a 300 m."
            />

            <Reveal>
              <div
                className="rounded-md bg-blue-900 text-white p-5 sm:p-8"
                style={laneLinesDark}
              >
                <div className="grid grid-cols-11 gap-1 sm:gap-1.5">
                  {Array.from({ length: 11 }, (_, w) => {
                    const isTestWeek = w === 0 || w === 3 || w === 10;
                    const c = phaseColors[w < 4 ? 0 : w < 7 ? 1 : 2];
                    return (
                      <div
                        key={w}
                        className={
                          isTestWeek
                            ? "rounded-md h-12 sm:h-16 flex flex-col items-center justify-end gap-0.5 pb-1.5 bg-white/10 text-volt font-mono text-[10px] sm:text-xs font-semibold"
                            : `${c.bar} ${c.text} rounded-md h-12 sm:h-16 flex items-end justify-center pb-1.5 font-mono text-[10px] sm:text-xs font-semibold`
                        }
                      >
                        {isTestWeek && (
                          <span className="uppercase tracking-widest text-[8px] sm:text-[10px] leading-none">
                            Test
                          </span>
                        )}
                        {w + 1}
                      </div>
                    );
                  })}
                </div>
                <p className="font-mono text-[11px] sm:text-xs uppercase tracking-widest text-white/50 mt-3 text-center">
                  Tests in weeks 1, 4 and 11 · 20 m, 30 m fly, 300 m
                </p>
              </div>
            </Reveal>

            <ol className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              {phases.map((phase, i) => (
                <Reveal key={phase.weeks} delay={i * 100} as="li">
                  <div className="relative h-full overflow-hidden rounded-md border border-base-300 p-6">
                    <span
                      aria-hidden="true"
                      className={`absolute inset-x-0 top-0 h-2 ${phaseColors[i].bar}`}
                    />
                    <p className="font-mono text-sm font-medium text-base-content/60">
                      {phase.weeks}
                    </p>
                    <h3 className="text-2xl font-bold font-grotesk mt-2 mb-2">
                      <Copy text={phase.title} />
                    </h3>
                    <p className="text-base-content/70 font-grotesk leading-relaxed">
                      <Copy text={phase.body} numbers />
                    </p>
                  </div>
                </Reveal>
              ))}
            </ol>

            <div className="mt-20">
              <Reveal className="mb-6">
                <div className="flex items-end gap-3">
                  <h3 className="text-3xl font-extrabold font-heading">
                    A sample week
                  </h3>
                  <HandNote className="text-primary -rotate-6 mb-1">
                    {handNotes.program}
                  </HandNote>
                </div>
                <p className="mt-3 font-grotesk text-base-content/70">
                  {sampleWeekNote}
                </p>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {sampleWeek.map((session, i) => (
                  <Reveal key={session.day} delay={i * 100}>
                    <div className="h-full rounded-md bg-base-200 p-6 transition-transform duration-300 hover:-translate-y-1">
                      <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest">
                        <span className="rounded-md bg-blue-900 text-white px-3 py-1">
                          {session.day}
                        </span>
                        {session.duration ? (
                          <span className="text-base-content/60">
                            <Copy text={session.duration} />
                          </span>
                        ) : null}
                      </div>
                      <h4 className="text-xl font-bold font-grotesk mt-4 mb-4">
                        <Copy text={session.title} />
                      </h4>
                      <ul className="flex flex-col gap-2 border-t-2 border-dashed border-base-300 pt-4">
                        {session.items.map((item) => (
                          <li
                            key={item}
                            className="flex gap-2 text-sm font-grotesk"
                          >
                            <CheckIcon />
                            <span>
                              <Copy text={item} numbers />
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Sports */}
        <section
          id="sports"
          className="scroll-mt-24 bg-base-200 py-16 md:py-24 px-4 sm:px-6"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              eyebrow="Built for your sport"
              title="Alongside the sport you play"
              intro="One program, three codes. It complements your club training. It doesn't replace it."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {sports.map((sport, i) => (
                <Reveal key={sport.title} delay={i * 100}>
                  <div
                    className={`h-full rounded-md p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${
                      i % 2 ? "hover:rotate-1" : "hover:-rotate-1"
                    } ${sportStyles[i]}`}
                  >
                    <h3 className="text-4xl font-extrabold font-heading uppercase tracking-tight leading-none">
                      {sport.title}
                    </h3>
                    <p className="font-grotesk leading-relaxed mt-3 opacity-85">
                      {sport.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 text-sm text-base-content/70 font-grotesk">
              <Copy text={inSeasonNote} />
            </p>
          </div>
        </section>

        {testimonials.length > 0 && (
          <section id="results" className="scroll-mt-24 py-16 md:py-24 px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <SectionHeading eyebrow="Results" title="From athletes who've done it" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t, index) => (
                  <Reveal key={index} delay={index * 100}>
                    <figure
                      className={`h-full rounded-md bg-base-100 border border-base-300 shadow-lg p-7 flex flex-col transition-transform duration-300 hover:rotate-0 hover:-translate-y-1 ${noteTilts[index % noteTilts.length]}`}
                    >
                      {t.result && (
                        <p className="self-start rounded-md bg-volt text-blue-900 font-mono text-sm font-semibold px-3 py-1 mb-4">
                          <Copy text={t.result} />
                        </p>
                      )}
                      <span className="font-heading text-6xl leading-none text-primary/30 -mb-4">
                        &ldquo;
                      </span>
                      <blockquote className="font-grotesk text-lg leading-relaxed flex-1">
                        <Copy text={t.quote} />
                      </blockquote>
                      <figcaption className="mt-6 text-sm font-grotesk">
                        <span className="font-semibold block">
                          <Copy text={t.name} />
                        </span>
                        <span className="text-base-content/60">
                          <Copy text={t.detail} />
                        </span>
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Coach */}
        <section
          id="coach"
          className="scroll-mt-24 bg-blue-900 text-white py-20 md:py-28 px-4 sm:px-6"
          style={laneLinesDark}
        >
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-14 md:gap-16 md:items-center">
            <Reveal className="shrink-0 mx-auto md:mx-0">
              <div className="relative rotate-2">
                <span className="absolute -top-3 left-6 w-16 h-6 bg-volt/80 -rotate-6 z-10" />
                <span className="absolute -bottom-3 right-6 w-16 h-6 bg-volt/80 rotate-3 z-10" />
                {coach.photo ? (
                  <img
                    src={coach.photo}
                    alt={coach.name}
                    className="w-60 h-72 md:w-72 md:h-80 rounded-md object-cover object-top shadow-2xl"
                  />
                ) : (
                  <div className="w-60 h-72 md:w-72 md:h-80 rounded-md bg-white/10 border-2 border-dashed border-white/30 flex items-center justify-center text-center px-4 text-sm font-grotesk shadow-2xl">
                    <Copy text="[Photo of Tori]" />
                  </div>
                )}
              </div>
            </Reveal>
            <Reveal delay={150}>
              <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-volt mb-4">
                <span className="w-2 h-2 rounded-full bg-volt" />
                Written by an Olympian
              </p>
              <h2 className="text-5xl md:text-7xl font-extrabold font-heading tracking-tight leading-none">
                {coach.name}
              </h2>
              <p className="text-white/60 font-mono text-sm mt-3">
                <Copy text={coach.role} numbers />
              </p>
              <p className="text-lg text-white/85 font-grotesk mt-6 leading-relaxed">
                <Copy text={coach.bio} />
              </p>
              <ul className="mt-6 flex flex-col gap-2.5">
                {coach.credentials.map((c) => (
                  <li key={c} className="flex gap-2 font-grotesk">
                    <CheckIcon className="text-volt" />
                    <span>
                      <Copy text={c} numbers />
                    </span>
                  </li>
                ))}
              </ul>
              <HandNote className="block text-5xl sm:text-6xl text-volt mt-8 -rotate-3">
                {handNotes.coach}
              </HandNote>
            </Reveal>
          </div>
        </section>

        {/* How it works */}
        <section id="process" className="scroll-mt-24 py-16 md:py-24 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <SectionHeading eyebrow="Getting started" title="How it works" />
            <ol className="relative grid grid-cols-1 md:grid-cols-3 gap-5">
              <li
                aria-hidden="true"
                className="hidden md:block absolute top-12 left-[16%] right-[16%] border-t-2 border-dashed border-base-300"
              />
              {steps.map((step, index) => (
                <Reveal key={step.title} delay={index * 120} as="li">
                  <div className="relative rounded-md bg-base-100 border border-base-300 p-7 h-full">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-blue-900 text-volt font-mono font-semibold">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-xl font-bold font-grotesk mt-5 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-base-content/70 font-grotesk leading-relaxed">
                      <Copy text={step.body} numbers />
                    </p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        {/* Offer */}
        <section
          id="offer"
          className="scroll-mt-24 bg-base-200 py-16 md:py-24 px-4 sm:px-6"
        >
          <Reveal className="relative max-w-xl mx-auto">
            <div className="hidden lg:flex absolute left-full ml-8 top-28 w-56 flex-col items-start text-primary rotate-6">
              <HandNote>{handNotes.offer}</HandNote>
              <HandArrow className="w-14 h-12 -scale-x-100 rotate-[40deg] -ml-4 mt-1" />
            </div>
            <div className="rounded-md bg-base-100 border-2 border-blue-900 shadow-[10px_10px_0_#003d55] p-8 sm:p-10">
              <p className="font-mono text-xs uppercase tracking-widest text-primary text-center">
                {offer.subtitle}
              </p>
              <h2 className="text-3xl font-extrabold font-heading text-center mt-3">
                {offer.title}
              </h2>
              <p className="text-center mt-6">
                <span className="font-mono text-7xl font-semibold tracking-tighter">
                  {priceAmount}
                </span>
                <span className="font-mono text-lg text-base-content/60 ml-2">
                  {priceCurrency}
                </span>
              </p>
              <HandNote className="lg:hidden block text-center text-primary mt-2 -rotate-2">
                {handNotes.offer}
              </HandNote>
              <ul className="mt-8 flex flex-col gap-3">
                {offer.includes.map((item) => (
                  <li key={item} className="flex gap-3 font-grotesk">
                    <CheckIcon />
                    <span>
                      <Copy text={item} numbers />
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <Link
                  to={ctaTo}
                  className={primaryButtonClass.replace("sm:w-auto", "")}
                >
                  <span>{ctaText}</span>
                  <ArrowIcon />
                </Link>
              </div>
            </div>
          </Reveal>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-24 py-16 md:py-24 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <SectionHeading title="Questions" center />
            <div>
              {faqItems.map((item, i) => (
                <FAQItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                  index={i}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 sm:px-6 pb-16 md:pb-24">
          <Reveal>
            <div
              className="relative max-w-5xl mx-auto rounded-[2rem] bg-blue-900 text-white px-6 py-14 sm:p-20 text-center overflow-hidden"
              style={laneLinesDark}
            >
              <div className="hidden md:block absolute inset-y-0 right-[5%] w-1 bg-volt/70" />
              <span className="hidden md:block absolute top-5 right-[5%] translate-x-1/2 font-mono text-xs text-volt">
                20 m
              </span>
              <HandNote className="block text-volt -rotate-3 mb-4">
                {handNotes.finalCta}
              </HandNote>
              <h2 className="relative text-4xl md:text-7xl font-extrabold font-heading tracking-tight leading-[1.02]">
                {finalCta.title}
              </h2>
              <p className="relative text-lg text-white/70 font-grotesk mt-5 mb-10">
                <Copy text={finalCta.body} numbers />
              </p>
              <div className="relative flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to={ctaTo}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold bg-volt text-blue-900 rounded-md font-grotesk shadow-[0_6px_0_#8fae1a] hover:-translate-y-0.5 hover:shadow-[0_8px_0_#8fae1a] active:translate-y-1 active:shadow-[0_2px_0_#8fae1a] transition-all focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-volt w-full sm:w-auto"
                >
                  <span>{ctaText}</span>
                  <ArrowIcon />
                </Link>
                <Link
                  to="/20m"
                  className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold bg-white text-blue-900 rounded-md font-grotesk shadow-[0_6px_0_rgba(0,0,0,0.35)] hover:-translate-y-0.5 hover:shadow-[0_8px_0_rgba(0,0,0,0.35)] active:translate-y-1 active:shadow-[0_2px_0_rgba(0,0,0,0.35)] transition-all focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white w-full sm:w-auto text-center"
                >
                  {sprintCtaLabel}
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-base-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-28 md:pb-12 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col items-center md:items-start gap-3">
            <BrandMark />
            <p className="text-base-content/70 text-sm font-grotesk text-center md:text-left">
              &copy; <span className="font-mono">{new Date().getFullYear()}</span>{" "}
              PEAK. All rights reserved. A project by Equil Labs Pty Ltd.
            </p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-4">
            <MonotoneSwitch on={monotone} onToggle={() => setMonotone((value) => !value)} />
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-grotesk">
              <a
                href="https://help.peak.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base-content/70 hover:text-base-content"
              >
                Help & Support
              </a>
              <a
                href="https://peak.app/privacy-policy"
                className="text-base-content/70 hover:text-base-content"
              >
                Privacy Policy
              </a>
              <a
                href="https://peak.app/terms-of-service"
                className="text-base-content/70 hover:text-base-content"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div
        className={`fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-base-300 bg-base-100/95 backdrop-blur px-4 py-3 transition-transform duration-300 ${
          showStickyCta ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!showStickyCta}
      >
        <div className="flex items-center gap-2">
          <Link
            to={ctaTo}
            tabIndex={showStickyCta ? 0 : -1}
            className={`${headerButtonClass} flex-1`}
          >
            {ctaTextShort}
          </Link>
          <Link
            to="/20m"
            tabIndex={showStickyCta ? 0 : -1}
            className="inline-flex flex-1 items-center justify-center px-3 py-2 text-sm font-semibold bg-volt text-blue-900 rounded-md font-grotesk hover:opacity-85 transition-opacity focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt whitespace-nowrap"
          >
            20m sprint test
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
